import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { Page } from '../lib/api';
import type { DeviceSummary } from '../lib/types';
import { useDebounced, useResource } from '../lib/useResource';
import { Card, Empty, Loading, Notice, Pill, StatusPill, when } from '../components/ui';

export function Devices() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [platform, setPlatform] = useState('');
  const [page, setPage] = useState(0);

  const query = useDebounced(search);

  const devices = useResource(
    () =>
      api.get<Page<DeviceSummary>>('/api/v1/devices', {
        query: query || undefined,
        status: status || undefined,
        platform: platform || undefined,
        page,
        size: 25,
      }),
    [query, status, platform, page],
  );

  return (
    <>
      <div className="page-head">
        <h1>Devices</h1>
        <p>Every company-issued laptop and desktop enrolled with the platform.</p>
      </div>

      <Card>
        <div className="filters">
          <label className="field grow">
            <span>Search</span>
            <input
              type="search"
              value={search}
              placeholder="Asset tag, hostname or serial number"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
            />
          </label>

          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
              <option value="">Any</option>
              {['ACTIVE', 'QUARANTINED', 'LOST', 'WIPE_PENDING', 'WIPED', 'RETIRED'].map((value) => (
                <option key={value} value={value}>{value.replace('_', ' ').toLowerCase()}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Platform</span>
            <select value={platform} onChange={(event) => { setPlatform(event.target.value); setPage(0); }}>
              <option value="">Any</option>
              <option value="MACOS">macOS</option>
              <option value="WINDOWS">Windows</option>
            </select>
          </label>
        </div>

        {devices.error && <Notice kind="error">{devices.error}</Notice>}
        {devices.loading && !devices.data && <Loading />}
        {devices.data?.content.length === 0 && (
          <Empty>No devices match these filters.</Empty>
        )}

        {devices.data && devices.data.content.length > 0 && (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Assigned to</th>
                    <th>Platform</th>
                    <th>Status</th>
                    <th>Baseline</th>
                    <th>Last seen</th>
                    <th>Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.data.content.map((device) => (
                    <tr
                      key={device.id}
                      className="clickable"
                      onClick={() => navigate(`/devices/${device.id}`)}
                    >
                      <td>
                        <span className={`dot ${device.online ? 'on' : 'off'}`} />
                        <strong>{device.assetTag}</strong>
                        <div style={{ color: 'var(--faint)', fontSize: 12 }}>{device.hostname ?? '—'}</div>
                      </td>
                      <td>{device.assignedStaffName ?? <span style={{ color: 'var(--faint)' }}>unassigned</span>}</td>
                      <td>
                        {device.platform === 'MACOS' ? 'macOS' : 'Windows'}
                        <div style={{ color: 'var(--faint)', fontSize: 12 }}>{device.osVersion ?? ''}</div>
                      </td>
                      <td><StatusPill value={device.status} /></td>
                      <td>{renderBaseline(device)}</td>
                      <td>{when(device.lastSeenAt)}</td>
                      <td className="mono">{device.agentVersion ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pager">
              <span>
                {devices.data.totalElements} device(s) · page {devices.data.page + 1} of{' '}
                {Math.max(1, devices.data.totalPages)}
              </span>
              <button
                type="button"
                className="secondary small"
                disabled={page === 0}
                onClick={() => setPage((value) => Math.max(0, value - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="secondary small"
                disabled={page + 1 >= devices.data.totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

function renderBaseline(device: DeviceSummary) {
  if (device.compliant === null || device.complianceScore === null) {
    return <span style={{ color: 'var(--faint)' }}>not assessed</span>;
  }
  return (
    <>
      <Pill tone={device.compliant ? 'ok' : device.complianceScore < 50 ? 'stop' : 'warn'}>
        {device.compliant ? 'meets baseline' : `${device.complianceScore}/100`}
      </Pill>
    </>
  );
}
